import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useLicenseColumnToggles } from '../../../hooks/useLicenseColumnToggles';

interface ColumnDef {
  key: string;
  labelKey: string;
}

const OPTIONAL_COLUMNS: ColumnDef[] = [
  { key: 'publisher', labelKey: 'App.Admin.Licenses.LicenseColumnToggleMenu.ColPublisher' },
  { key: 'activation', labelKey: 'App.Admin.Licenses.LicenseColumnToggleMenu.ColActivationDate' },
  { key: 'lastActive', labelKey: 'App.Admin.Licenses.LicenseColumnToggleMenu.ColLastActivity' },
  { key: 'externalLicenseId', labelKey: 'App.Admin.Licenses.LicenseColumnToggleMenu.ColExternalId' },
  { key: 'bookseller', labelKey: 'App.Admin.Licenses.LicenseColumnToggleMenu.ColBookseller' },
  { key: 'studentName', labelKey: 'App.Admin.Licenses.LicenseColumnToggleMenu.ColStudentName' },
];

export default function LicenseColumnToggleMenu() {
  const { t } = useTranslation();
  const { isHidden, toggleColumn } = useLicenseColumnToggles();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="border border-neutral-300 rounded px-2 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {t('App.Admin.Licenses.LicenseColumnToggleMenu.ButtonLabel')}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 z-10 bg-white border border-neutral-200 rounded shadow-md py-1 w-48">
          {OPTIONAL_COLUMNS.map(col => (
            <label
              key={col.key}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={!isHidden(col.key)}
                onChange={() => toggleColumn(col.key)}
              />
              {t(col.labelKey)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
